import {
  BetweenItemDropBorder,
  type BetweenItemDropBorderProps,
} from 'components/BetweenItemDropBorder';
import {
  type DraggableItem,
  type DropResult,
  useDrop,
  CUSTOM_DRAGGABLE_ITEM_TYPES,
  DROP_RESULT_TYPES,
} from 'components/customDragLayer';

export type BetweenVisibleColumnsDropBorderProps = Pick<
  BetweenItemDropBorderProps,
  'side'
> & {
  queryParamName: string;
  position: number;
  onReorderVisibleColumns: (oldPosition: number, nextPosition: number) => void;
};

export const BetweenVisibleColumnDropBorder = ({
  side,
  position,
  onReorderVisibleColumns,
}: BetweenVisibleColumnsDropBorderProps) => {
  const handleDrop = (
    droppedItem: DraggableItem<
      typeof CUSTOM_DRAGGABLE_ITEM_TYPES.VIEWS_VISIBLE_COLUMNS
    >,
  ): DropResult | undefined => {
    const droppedColumn = droppedItem.visibleColumns[0];

    const { position: oldPosition } = droppedColumn;

    const nextPosition = position;

    // Neither dropping an item on the border immediately before it (same
    // position) nor the border immediately after it (position + 1) changes
    // the order
    if (nextPosition === oldPosition || nextPosition === oldPosition + 1) {
      const dropResult = {
        dropResultType: DROP_RESULT_TYPES.VIEWS_REORDER_VISIBLE_COLUMNS,
        didReorder: false,
      };

      return dropResult;
    }

    onReorderVisibleColumns(oldPosition, nextPosition);

    const dropResult = {
      dropResultType: DROP_RESULT_TYPES.VIEWS_REORDER_VISIBLE_COLUMNS,
      didReorder: true,
    };

    return dropResult;
  };

  const [{ canDrop, isOver }, connectDropTarget] = useDrop({
    accept: [CUSTOM_DRAGGABLE_ITEM_TYPES.VIEWS_VISIBLE_COLUMNS],
    canDrop: () => true,
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver({ shallow: true }),
    }),
    onDropEnd: handleDrop,
  });

  return (
    <BetweenItemDropBorder
      ref={connectDropTarget}
      side={side}
      canDrop={canDrop}
      isOver={isOver}
      dropAreaProps={{ height: 5 }}
    />
  );
};
